import styled, {css, keyframes} from "styled-components";

/**
 * PatchUnlockModal styled components and animations
 *
 * This module contains all styled components and keyframe animations
 * used in the patch unlocking mini-games (tree chopping and boulder cracking).
 */

export const TREE_ICON_SIZE = 80;
export const BOULDER_ICON_SIZE = 72;

export const flash = keyframes`
  from { filter: brightness(1.2); }
  50% { filter: brightness(1.6); }
  to { filter: brightness(1); }
`;

export const shake = keyframes`
  10% { transform: translate(-1px, -1px) rotate(-1deg); }
  20% { transform: translate(2px, 0) rotate(1deg); }
  30% { transform: translate(1px, 1px) rotate(0deg); }
  40% { transform: translate(-1px, 1px) rotate(1deg); }
  50% { transform: translate(1px, -1px) rotate(-1deg); }
`;

export const GameWrapper = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 16px;
  margin-top: 12px;
`;

export const TreeCard = styled.button<{ $broken: boolean, $flash: boolean }>`
  background: ${({$broken}) => $broken ? "#8b5a2b" : "linear-gradient(135deg, #2d6a1f, #3c8c2c)"};
  border: 3px solid #1e4616;
  border-radius: 10px;
  min-height: 170px;
  padding: 12px;
  color: white;
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.25);
  position: relative;
  overflow: hidden;
  ${({$flash}) => $flash ? css`animation: ${flash} 0.35s ease-in-out;` : ""}
`;

export const Boulder = styled.button<{ $flash: boolean }>`
  background: radial-gradient(circle at 30% 30%, #6b6b6b, #4c4c4c);
  border: 3px solid #2f2f2f;
  border-radius: 14px;
  min-height: 210px;
  padding: 16px;
  color: white;
  display: flex;
  flex-direction: column;
  gap: 10px;
  align-items: center;
  justify-content: center;
  cursor: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24'%3E%3Cpath d='M4 20l6.5-6.5-2.5-2.5L2 14l2 6zM14.5 4.5l5 5c.66.66.66 1.74 0 2.4l-2.6 2.6-7.4-7.4 2.6-2.6c.66-.66 1.74-.66 2.4 0z' fill='%23ffffff'/%3E%3C/svg%3E") 12 12, pointer;
  position: relative;
  ${({$flash}) => $flash ? css`animation: ${shake} 0.25s ease-in-out;` : ""}
`;

export const CrackCard = styled.button<{ $complete: boolean, $flash: boolean }>`
  background: ${({$complete}) => $complete ? "#c0b7a4" : "#525252"};
  border: 3px solid ${({$complete}) => $complete ? "#7e775f" : "#2f2f2f"};
  border-radius: 14px;
  min-height: 210px;
  padding: 12px;
  color: white;
  display: flex;
  flex-direction: column;
  gap: 12px;
  align-items: center;
  justify-content: center;
  cursor: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24'%3E%3Cpath d='M4 20l6.5-6.5-2.5-2.5L2 14l2 6zM14.5 4.5l5 5c.66.66.66 1.74 0 2.4l-2.6 2.6-7.4-7.4 2.6-2.6c.66-.66 1.74-.66 2.4 0z' fill='%23ffffff'/%3E%3C/svg%3E") 12 12, pointer;
  position: relative;
  box-shadow: 0 6px 14px rgba(0, 0, 0, 0.25);
  overflow: hidden;
  ${({$flash}) => $flash ? css`animation: ${shake} 0.25s ease-in-out;` : ""}
`;

export const CrackImage = styled.img`
  height: ${TREE_ICON_SIZE}px;
  width: ${TREE_ICON_SIZE}px;
  object-fit: contain;
  filter: drop-shadow(0 4px 6px rgba(0, 0, 0, 0.35));
`;

export const LootBadge = styled.div`
  position: absolute;
  top: 10px;
  right: 10px;
  background: linear-gradient(135deg, #ffe066, #ff8c42);
  color: #1b1b1b;
  padding: 8px 10px;
  border-radius: 12px;
  font-weight: 700;
  box-shadow: 0 6px 14px rgba(0, 0, 0, 0.25);
  animation: ${flash} 2s ease-in-out;
`;
